<template>
  <div>
    <b-row>
      <b-col md="12">
        <div class="table-notes-title" v-bind:style="headerStyle" v-if="header">
            <div class="text-truncate t-header" v-if="headerTitle">{{ headerTitle }}</div>
            <div class="btn-group btn-group-sm float-sm-right" role="group" aria-label="Table options">
                <b-tooltip
                    v-if="addAction.visible && addAction.tooltip"
                    :target="'add-action-button'"
                    placement="top"
                    :delay="{ show: 1500, hide: 50 }"
                >
                    {{ addAction.tooltip }}
                </b-tooltip>
                <b-button id= "add-action-button" class="table-title-btn" @click="addHandler" v-if="addAction.visible">{{ addAction.title }}</b-button>
            </div>
        </div>
        
        <div class="medezTables cutescroll table-responsive" :class="{'table-invalid': invalid}">
          <table role="table" class="table b-table b-table-selectable b-table-select-single" aria-multiselectable="false" aria-busy="false" aria-colcount="rows.length">
            <thead>
              <tr>
                <th v-if="actions.length !== 0">
                  <div>{{ actionLabel }}</div>
                </th>
                <th 
                  v-for="(field, index) in fields"
                  :key="`field_${index}`"
                ><div>{{ field.label }}</div></th>
              </tr>
            </thead>
          
            <tbody>
              <tr 
                v-for="(item, rowIndex) in rows" 
                :key="`row_${rowIndex}`"
                :class="{ 'b-table-row-selected rowSelected': isRowSelected(rowIndex), 'b-table-row-selected rowInserted': isRowNew(rowIndex) }"
              >
                <!-- Action Column -->
                <td class="editable-cell" v-if="actions.length !== 0">
                  <div class="text-center">
                    <template v-for="(action, idx) in actions">
                      <b-tooltip
                          v-if="action.tooltip && actionVisible(action, item)"
                          :key="'tooltip-' + rowIndex + '-' + idx"
                          :target="'action-' + rowIndex + '-' + idx"
                          placement="top"
                          :delay="{ show: 1500, hide: 50 }"
                      >
                          {{ action.tooltip }}
                      </b-tooltip>

                      <button 
                        v-if="actionVisible(action, item)"
                        class="btn btn-sm btn-light"
                        :key="'action-' + rowIndex + '-' + idx"
                        :id="'action-' + rowIndex + '-' + idx"
                        :disabled="actionDisabled(action, item)"
                        @click="() => handleActionClick(action, item, rowIndex)"
                      >
                        <i :class="action.icon"></i>
                      </button>
                    </template>
                  </div>
                </td>

                <template v-for="(field, fieldIndex) in fields">
                  <EditableNumericCell 
                    v-if="field.dataType === 'integer'"
                    :key="`field_integer_${rowIndex}_${fieldIndex}`"
                    inputType="integer"
                    v-model="item[field.key]"
                    :name="`cell-${rowIndex}-${fieldIndex}`"
                    :ref="`cell-${rowIndex}-${fieldIndex}`"
                    :editable="(field.editable === undefined || field.editable) && readonly === false"
                    :min="field.props ? field.props.min : undefined"
                    :max="field.props ? field.props.max: undefined"
                    :step="field.props ? field.props.step: undefined"
                    :formatter="field.formatter ? formatterHandle(field.formatter, item) : null"
                    :isFocused="activeCell === `cell-${rowIndex}-${fieldIndex}`"
                    :validatorRules="field.validator"
                    @nextCell="focusCell(rowIndex, fieldIndex+1)"
                    @prevCell="focusCell(rowIndex, fieldIndex-1)"
                    @upCell="focusCell(rowIndex-1, fieldIndex)"
                    @downCell="focusCell(rowIndex+1, fieldIndex)"
                    @cellFocus="setActiveCell"
                    @requestEdit="handleRequestEdit"
                    @validationState="updateValidation"
                  />
                  <EditableNumericCell 
                    v-else-if="field.dataType === 'decimal'"
                    :key="`field_decimal_${rowIndex}_${fieldIndex}`"
                    inputType="decimal"
                    v-model="item[field.key]"
                    :name="`cell-${rowIndex}-${fieldIndex}`"
                    :ref="`cell-${rowIndex}-${fieldIndex}`"
                    :editable="(field.editable === undefined || field.editable) && readonly === false"
                    :min="field.props ? field.props.min : undefined"
                    :max="field.props ? field.props.max: undefined"
                    :step="field.props ? field.props.step: undefined"
                    :formatter="field.formatter ? formatterHandle(field.formatter, item) : null"
                    :isFocused="activeCell === `cell-${rowIndex}-${fieldIndex}`"
                    :validatorRules="field.validator"
                    @nextCell="focusCell(rowIndex, fieldIndex+1)"
                    @prevCell="focusCell(rowIndex, fieldIndex-1)"
                    @upCell="focusCell(rowIndex-1, fieldIndex)"
                    @downCell="focusCell(rowIndex+1, fieldIndex)"
                    @cellFocus="setActiveCell"
                    @requestEdit="handleRequestEdit"
                    @validationState="updateValidation"
                  />
                  <EditableSelectCell 
                    v-else-if="field.dataType === 'dropdown'"
                    :key="`field_select_${rowIndex}_${fieldIndex}`"
                    v-model="item[field.key]"
                    :name="`cell-${rowIndex}-${fieldIndex}`"
                    :ref="`cell-${rowIndex}-${fieldIndex}`"
                    v-bind:editable="(field.editable === undefined || field.editable) && readonly === false"
                    v-bind:options="field.props.options"
                    v-bind:placeholder="field.props.placeholder"
                    @next-cell="focusNext(fieldIndex + 1 === fields.length ? rowIndex + 1 : rowIndex, fieldIndex + 1 === fields.length ? 1 : fieldIndex + 1)"
                    @focus-row="focusRow"
                  />
                  <EditableLookupCell 
                    v-else-if="field.dataType === 'lookup'"
                    :key="`field_lookup_${rowIndex}_${fieldIndex}`"
                    v-model="item[field.key]"
                    :name="`cell-${rowIndex}-${fieldIndex}`"
                    :ref="`cell-${rowIndex}-${fieldIndex}`"
                    :editable="(field.editable === undefined || field.editable) && readonly === false"
                    :formatter="field.formatter ? formatterHandle(field.formatter, item) : null"
                    :api="field.props.api"
                    :fieldCode="field.props.fieldCode"
                    :fieldDescription="field.props.fieldDescription"
                    :searchTitle="field.props.searchTitle"
                    :searchFieldCode="field.props.searchFieldCode"
                    :value-description.sync="item[field.props.modelDescription]"
                    :isFocused="activeCell === `cell-${rowIndex}-${fieldIndex}`"
                    :validatorRules="field.validator"
                    @nextCell="focusCell(rowIndex, fieldIndex+1)"
                    @prevCell="focusCell(rowIndex, fieldIndex-1)"
                    @upCell="focusCell(rowIndex-1, fieldIndex)"
                    @downCell="focusCell(rowIndex+1, fieldIndex)"
                    @cellFocus="setActiveCell"
                    @requestEdit="handleRequestEdit"
                    @validationState="updateValidation"
                  />
                  <EditableDateCell 
                    v-else-if="field.dataType === 'date'"
                    :key="`field_date_${rowIndex}_${fieldIndex}`"
                    v-model="item[field.key]"
                    :name="`cell-${rowIndex}-${fieldIndex}`"
                    :ref="`cell-${rowIndex}-${fieldIndex}`"
                    :editable="(field.editable === undefined || field.editable) && readonly === false"
                    :isFocused="activeCell === `cell-${rowIndex}-${fieldIndex}`"
                    :validatorRules="field.validator"
                    @nextCell="focusCell(rowIndex, fieldIndex+1)"
                    @prevCell="focusCell(rowIndex, fieldIndex-1)"
                    @upCell="focusCell(rowIndex-1, fieldIndex)"
                    @downCell="focusCell(rowIndex+1, fieldIndex)"
                    @cellFocus="setActiveCell"
                    @requestEdit="handleRequestEdit"
                    @validationState="updateValidation"
                  />
                  <EditableTimeCell 
                    v-else-if="field.dataType === 'time'"
                    :key="`field_time_${rowIndex}_${fieldIndex}`"
                    v-model="item[field.key]"
                    :name="`cell-${rowIndex}-${fieldIndex}`"
                    :ref="`cell-${rowIndex}-${fieldIndex}`"
                    v-bind:timeFormat="field.props.timeFormat"
                    v-bind:precision="field.props.precision"
                    :editable="(field.editable === undefined || field.editable) && readonly === false"
                    :isFocused="activeCell === `cell-${rowIndex}-${fieldIndex}`"
                    :validatorRules="field.validator"
                    @nextCell="focusCell(rowIndex, fieldIndex+1)"
                    @prevCell="focusCell(rowIndex, fieldIndex-1)"
                    @upCell="focusCell(rowIndex-1, fieldIndex)"
                    @downCell="focusCell(rowIndex+1, fieldIndex)"
                    @cellFocus="setActiveCell"
                    @requestEdit="handleRequestEdit"
                    @validationState="updateValidation"
                  />
                  <EditableDateTimeCell 
                    v-else-if="field.dataType === 'datettime'"
                    :key="`field_datetime_${rowIndex}_${fieldIndex}`"
                    v-model="item[field.key]"
                    :name="`cell-${rowIndex}-${fieldIndex}`"
                    :ref="`cell-${rowIndex}-${fieldIndex}`"
                    :timeFormat="field.props.timeFormat"
                    :precision="field.props.precision"
                    :editable="(field.editable === undefined || field.editable) && readonly === false"
                    :isFocused="activeCell === `cell-${rowIndex}-${fieldIndex}`"
                    :validatorRules="field.validator"
                    @nextCell="focusCell(rowIndex, fieldIndex+1)"
                    @prevCell="focusCell(rowIndex, fieldIndex-1)"
                    @upCell="focusCell(rowIndex-1, fieldIndex)"
                    @downCell="focusCell(rowIndex+1, fieldIndex)"
                    @cellFocus="setActiveCell"
                    @requestEdit="handleRequestEdit"
                    @validationState="updateValidation"
                  />
                  <EditableInputCell 
                    v-else-if="field.dataType === 'text'"
                    :key="`field_input_${rowIndex}_${fieldIndex}`"
                    v-model="item[field.key]"
                    :name="`cell-${rowIndex}-${fieldIndex}`"
                    :ref="`cell-${rowIndex}-${fieldIndex}`"
                    :editable="(field.editable === undefined || field.editable) && readonly === false"
                    :formatter="field.formatter ? formatterHandle(field.formatter, item) : null"
                    :isFocused="activeCell === `cell-${rowIndex}-${fieldIndex}`"
                    :validatorRules="field.validator"
                    @nextCell="focusCell(rowIndex, fieldIndex+1)"
                    @prevCell="focusCell(rowIndex, fieldIndex-1)"
                    @upCell="focusCell(rowIndex-1, fieldIndex)"
                    @downCell="focusCell(rowIndex+1, fieldIndex)"
                    @cellFocus="setActiveCell"
                    @requestEdit="handleRequestEdit"
                    @validationState="updateValidation"
                  />
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <div v-if="showMessageIsEmpty && totalRows === 0" v-bind:style="emptyRecordsStyle" role="alert" aria-live="polite">
          <div class="text-center my-2">{{emptyMessage}}</div>
        </div>
      </b-col>
    </b-row>

    <b-row dir="ltr" v-if="totalRows !== 0">
      <b-col v-if="pageOptions" sm="7" md="6" class="my-1">
        <b-pagination 
          v-model="page"
          :total-rows="totalRows"
          :per-page="perPage"
          align="fill"
          size="sm"
          class="my-0"
          @change="pageChangedHandler"
        >
        </b-pagination>
      </b-col>
      <b-col v-if="pageOptions" align="right" class="mr-2">
        <b-form-group
        >{{ $t('Shared.Show') }}
          <b-form-select
            v-model="perPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
            style="width:auto;"
            @change="pageSizeHandler($event)"
          ></b-form-select>
            {{ $t('Shared.Of') }} {{totalRows}}
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Utils from "@/common/utils";
import ShowMessage from '@/components/messages/ShowMessage';
import EditableInputCell from './EditableInputCell.vue';
import EditableNumericCell from './EditableNumericCell.vue';
import EditableSelectCell from './EditableSelectCell.vue';
import EditableLookupCell from './EditableLookupCell.vue';
import EditableDateCell from './EditableDateCell.vue';
import EditableDateTimeCell from './EditableDateTimeCell.vue';
import EditableTimeCell from './EditableTimeCell.vue';
import { EventBus } from '@/event-bus.js';

export default {
    name: 'MdEditableGrid',
    mixins: [ShowMessage],

    components: {
        EditableInputCell, 
        EditableNumericCell,
        EditableSelectCell,
        EditableLookupCell,
        EditableDateCell,
        EditableDateTimeCell,
        EditableTimeCell
    },

    props: {
      fields: {
        type: Array,
        required: true,
        validator: (value) => {
          return value.every(field => (
            typeof field.key === 'string' &&
            typeof field.label === 'string' &&
            (field.editable === undefined || typeof field.editable === 'boolean') &&
            (field.class === undefined || typeof field.class === 'string') &&
            (field.dataType === undefined || typeof field.dataType === 'string') &&
            (field.formatter === undefined || typeof field.formatter === 'function') &&
            (field.props === undefined || typeof field.props === 'object') 
          ));
        }
      },
      value: {
        type: Array,
        required: true,
      },
      customActions: {
        type: Array,
        default: () => [],
        validator: (value) => {
          return value.every(action => (
            typeof action.visible === 'boolean' &&
            typeof action.icon === 'string' &&
            typeof action.event === 'function'
          ));
        }
      },
      readonly: {
        type: Boolean,
        default: false
      },
      add: {
        type: Object,
        default: () => ({
          visible: false,
          title: 'Add item',
          handler: null,
          tooltip: null
        }),
        validator: (value) => {
          return typeof value.visible === 'boolean' ||
            value.handler === undefined || typeof value.handler === 'function';
        }
      },
      edit: {
        type: Object,
        default: () => ({
          visible: false,
          icon: 'fas fa-edit',
          handler: null,
          tooltip: null,
          disabled: null
        }),
        validator: (value) => {
          return typeof value.visible === 'boolean' || typeof value.visible === 'function' || 
            typeof value.handler === 'function' || typeof value.disabled === 'function';
        }
      },
      delete: {
        type: Object,
        default: () => ({
          visible: false,
          icon: 'fas fa-trash',
          handler: null,
          tooltip: null,
          disabled: null
        }),
        validator: (value) => {
          return typeof value.visible === 'boolean' || typeof value.visible === 'function' || 
            typeof value.handler === 'function' || typeof value.disabled === 'function';
        }
      },
      print: {
        type: Object,
        default: () => ({
          visible: false,
          icon: 'fas fa-print',
          handler: null,
          tooltip: null,
          disabled: null
        }),
        validator: (value) => {
          return typeof value.visible === 'boolean' || typeof value.visible === 'function' || 
            typeof value.handler === 'function' || typeof value.disabled === 'function';
        }
      },
      header: {
        type: Boolean,
        default: true
      },
      headerTitle: {
        type: String,
        default: null
      },
      headerStyle: {
        type: String,
        default: ''
      },
      perPage: {
        type: Number,
        default: 10
      },
      pageChanged: Function,
      perPageFunction: Function,
      pageOptions: {
          type: Array,
          default: () => [5, 10, 15]
      },
      totalRows: {
        type: Number,
        default: 0
      },
      currentPage: {
        type: Number,
        default: 1
      },
      showMessageIsEmpty: {
        type: Boolean,
        default: false
      },
      emptyText: {
        type: String,
        default: null
      },
      validateRowCallback: {
        type: Function,
        default: null
      }
    },

    data() {
        return {
            page: 1,
            emptyRecordsStyle: {
              backgroundColor: '#CF0',
              fontWeight: 'bold !important',
              textTransform: 'uppercase',
              padding: '2px',
              borderRadius: '50px',
              animation: 'fadeOut 10s ease-in-out infinite'
            }, 
            invalid: false,
            //selectedRow: -1,
            activeCell: null,
            activeRow: null,
            addedRows: [],
            cellValidation: {}
        };
    },

    watch: {
      currentPage(newValue) {
        this.$nextTick(() => this.page = newValue);
      }
    },

    computed: {
      actions() {
        return [
          {
            id: 'edit',
            visible: false,
            icon: 'fas fa-edit',
            handler: this.edit.handler,
            disabled: this.edit.disabled,
            tooltip: this.$t('Shared.Edit'),
            ...this.edit
          },
          {
            id: 'delete',
            visible: false,
            icon: 'fas fa-trash',
            handler: this.edit.handler,
            disabled: this.edit.disabled,
            tooltip: this.$tc('Shared.DelRecord', 2),
            ...this.delete
          },
          {
            id: 'print',
            visible: false,
            icon: 'fas fa-print',
            handler: this.edit.handler,
            disabled: this.edit.disabled,
            tooltip: this.$t('Shared.Report'),
            ...this.print
          },
          ...this.customActions
        ]
        .filter(action => action.visible && (typeof action.handler === 'function'));
      },
      actionLabel() {
        return this.$tc('Shared.ActionsTxt', 2);
      },
      emptyMessage() {
        return this.emptyText ?? this.$t('MdTable.nRecShow')
      },
      addAction() {
        return {
          visible: false,
          title: this.$t('Shared.Add'),
          tooltip: this.$t('Shared.Add'),
          ...this.add
        }
      },
      rows() {
        return this.addedRows.concat(this.value);
      },
      editableFields() {
        return this.fields
              .map((field, index) => ({index, editable: field.editable}))
              .filter((field) => field.editable === undefined || field.editable === true)
              .map((element) => (element.index));
      },
      selectedRow() {
        if (this.activeCell) {
          const data = this.activeCell.split('-');

          if (data.length === 3) {
            return data[1];
          }
        }

        return null;
      }
    },

    methods: {
      setActiveCell(row, name) { 
        // si la celda activa actual es inválida, no permitas cambiar 
        if (this.activeCell && this.cellValidation[this.activeCell]) { 
          const invalidCell = this.$refs[this.activeCell][0]; 
          
          if (invalidCell && name != this.activeCell) {
            invalidCell.startEdit(false);
          }
          
          return; 
        }

        this.activeCell = name; 
      }, 
      
      handleRequestEdit({ name }) { 
        // mismo chequeo: no salir de celda inválida 
        if (this.activeCell && this.cellValidation[this.activeCell]) { 
          return; 
        } 
        
        this.activeCell = name; // ahora sí, ordena al hijo que entre en edición 
        const cell = this.$refs[name][0]; 
        cell.startEdit(); 
      }, 
      
      updateValidation({ name, invalid }) { 
        this.$set(this.cellValidation, name, invalid); 
      }, 

      focusCell(rowIndex, fieldIndex) { 
        // Validar límites 
        if (rowIndex < 0 || rowIndex >= this.rows.length) {
          return; 
        }

        if (fieldIndex < 0 || fieldIndex >= this.fields.length) {
          return;
        } 
        
        const name = `cell-${rowIndex}-${fieldIndex}`; 
        
        // Bloquear si la celda activa actual es inválida 
        if (this.activeCell && this.cellValidation[this.activeCell]) { 
          return; 
        } 
        
        this.activeCell = name; 
        const cell = this.$refs[name][0]; 
        
        if (cell) { 
          cell.startEdit();
        } 
      },

      isRowSelected(rIndex) {
        const selectedRow = Number(this.selectedRow);
        return rIndex === selectedRow && !this.isRowNew(rIndex);
      },

      isRowNew(rIndex) {
        return (rIndex + 1) <= this.addedRows.length;
      },




        /*focusRow(row) {
          if (row != -1) {
            this.selectedRow = row;
          }
        },*/

        focusNext(row, col) {
            if (this.readonly === false) {
                let targetCol = col;
                while (targetCol < this.fields.length) {
                  const field = this.fields[targetCol];
                  if (field.editable === undefined || field.editable) {
                    break;
                  }

                  targetCol++;
                }

                const targetRef = `cell-${row}-${targetCol}`;
                const cellComponent = this.$refs[targetRef];

                if (cellComponent) {
                    const instance = Array.isArray(cellComponent) ? cellComponent[0] : cellComponent;
                    instance.focusCell();
                } else if (row >= this.value.length) {
                    //this.addNewMember();
                    this.$nextTick(() => this.focusNext(row, col));
                }
            }
        },

        pageSizeHandler(perPage) {
          this.perPageFunction(perPage);
        },

        pageChangedHandler(page) {
            this.pageChanged(page);
        },

        invalidateState(state) {
          this.invalid = state;
        },

        actionVisible(action, item) {
          if (typeof action.visible === 'function') {
            try {
              return action.visible(item);
            } 
            catch (e) {
              return true;
            }
          }
          
          return action.visible !== false;
        },

        actionDisabled(action, item) {
          if (typeof action.disabled === 'function') {
            try {
              return action.disabled(item);
            } 
            catch (e) {
              return false;
            }
          }

          return !!action.disabled;
        },

        handleActionClick(action, item, index) {
          if (this.actionDisabled(action, item)) {
            return;
          }

          if (typeof action.handler === 'function') {
            if (this.canExecuteAction()) {
              action.handler(item, index);
            } else {
              this.showMessage(this.$tc('Msg.Warning', 1),this.$t('Msg.InvalidItemAction'), 'warning');
            }
          }
        },

        addHandler() {
          if (this.canExecuteAction()) {
            let newRow = this.createNewRow(); 
            if (this.addAction.handler) { 
              const handlerResult = this.addAction.handler(); 
              newRow = Utils.mergeWithPriority(newRow, handlerResult);
            }

            this.addedRows.unshift(newRow);
            this.$nextTick(() => this.focusCell(this.addedRows.length - 1, this.editableFields[0]));
            EventBus.$emit('onChanged', { changed: true, componentName: `cell-${this.addedRows.length - 1}-${this.editableFields[0]}`, value: '' });
          } else {
            this.showMessage(this.$tc('Msg.Warning', 1),this.$t('Msg.InvalidItemAdd'), 'warning');
          }
        },

        createNewRow() {
          const newRow = {};
          this.fields.forEach(element => {
            newRow[element.key] = element.defaultValue ? element.defaultValue : null;
          });

          return newRow;
        },

        isItemsValid(showMessageError = true) {
          let valid = true;
          this.rows.forEach((_, index) => {
            this.editableFields.forEach((fieldIndex) => {
              const targetRef = `cell-${index}-${fieldIndex}`;
              const cellComponent = this.$refs[targetRef];

              if (cellComponent) {
                const instance = Array.isArray(cellComponent) ? cellComponent[0] : cellComponent;
                const cellValid = instance.validateCell();
                valid &= cellValid;

                if (!cellValid && showMessageError) {
                  this.showMessage(this.$tc('Msg.Error'), instance.invalidMessage(instance.name), "errorAutoHide");
                }
              }
            });
          });

          return valid;
        },

        canExecuteAction() {
          return !this.activeCell || !this.cellValidation[this.activeCell];
        },

        formatterHandle(formatter, item) {
          return (value) => {
            if (formatter) {
              return formatter(value, item);
            }
            return value;
          }
        }
    }
}
</script>

<style scoped>
.editable-cell {
  padding: 0 !important;
  vertical-align: middle !important;
  min-width: 120px;
}
.table-invalid { border: 1px solid red; }
/*.editableTable { border-collapse: collapse; }*/
/*.editableTable td { border-bottom: 1px solid #ccc; border-right: 1px solid #ccc; }*/
/* Highlight selected row */
.rowSelected { background: linear-gradient(0deg, #19BEE8 3%, #D9F4FB 4%, #D9F4FB); }
.rowInserted { background: linear-gradient(0deg, #86ec67 3%, #e0f7d9 4%, #d0f5c5); }
/* Hover effect for rows */
/*.editableTable tr:hover { background-color: #f5f5f5; } */
</style>
